/**
 * Indicadores de alerta vinculados a los arts. 139 QUÁTER y 400 BIS del Código Penal Federal
 * (Acuerdo 115/2026). El catálogo es FIJO en el backend (`av_ebr_calibracion.CATALOGO_INDICADORES`);
 * la entidad activa los que aplican y asigna responsable y acción. Guardado con bitácora vía
 * `PUT /api/av/ebr/indicadores`.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Siren, Save, Loader2 } from "lucide-react";

import { api, apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";

const FACTOR_LABEL = {
  cliente: "Cliente/usuario", producto: "Producto/servicio", geografia: "Zona geográfica",
  transacciones: "Transacciones", canal: "Canal",
};

export function AvEbrIndicadores({ indicadores, articulos, puedeEditar, onSaved }) {
  const [lista, setLista] = useState(indicadores || []);
  const [saving, setSaving] = useState(false);

  const set = (clave, campo, val) => setLista((p) => p.map((i) => i.clave === clave ? { ...i, [campo]: val } : i));

  const guardar = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/av/ebr/indicadores", {
        indicadores: lista.map((i) => ({
          clave: i.clave, activo: !!i.activo, responsable: i.responsable || "",
          accion: i.accion || "", nota: i.nota || "",
        })),
      });
      toast.success("Indicadores 139 Quáter / 400 Bis guardados.");
      onSaved(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Card data-testid="av-ebr-indicadores">
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-2">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <Siren className="h-4 w-4 text-red-700" /> Indicadores de alerta por tipo penal
          </CardTitle>
          <p className="text-xs text-slate-500">
            Catálogo fijo por norma. Activa los que apliquen a tu actividad y define quién los
            atiende y con qué acción.
          </p>
        </div>
        {puedeEditar && (
          <Button size="sm" onClick={guardar} disabled={saving} className="bg-primary hover:bg-sidebar-hover"
            data-testid="av-indicadores-guardar">
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Guardar indicadores
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-5">
        {(articulos || []).map((art) => {
          const delArt = lista.filter((i) => i.articulo === art.clave);
          return (
            <div key={art.clave} data-testid={`av-indicadores-articulo-${art.clave}`}>
              <div className="mb-2 flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-900">
                {art.nombre}
                <Badge variant="outline" className="font-normal">{art.descripcion}</Badge>
                <span className="text-xs font-normal text-slate-500">
                  {delArt.filter((i) => i.activo).length} de {delArt.length} activos
                </span>
              </div>
              <div className="space-y-2">
                {delArt.map((ind) => (
                  <div key={ind.clave}
                    className={`rounded-md border p-3 ${ind.activo ? "border-red-200 bg-red-50/40" : "border-slate-200 bg-slate-50/60"}`}
                    data-testid={`av-indicador-${ind.clave}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-900">{ind.nombre}</p>
                        <p className="text-xs text-slate-600">{ind.descripcion}</p>
                        <p className="mt-1 text-xs text-slate-400">
                          Factor del EBR: {FACTOR_LABEL[ind.factor] || ind.factor}
                        </p>
                      </div>
                      <Switch checked={!!ind.activo} disabled={!puedeEditar}
                        data-testid={`av-indicador-switch-${ind.clave}`}
                        onCheckedChange={(v) => set(ind.clave, "activo", v)} />
                    </div>
                    {ind.activo && (
                      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <Input className="h-8" placeholder="Responsable de atenderlo" value={ind.responsable || ""}
                          disabled={!puedeEditar} data-testid={`av-indicador-resp-${ind.clave}`}
                          onChange={(e) => set(ind.clave, "responsable", e.target.value)} />
                        <Input className="h-8" placeholder="Acción al detectarlo (p. ej. escalar al OC y evaluar aviso)"
                          value={ind.accion || ""} disabled={!puedeEditar}
                          data-testid={`av-indicador-accion-${ind.clave}`}
                          onChange={(e) => set(ind.clave, "accion", e.target.value)} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
